"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

export default function SoulmachineWidget() {
  const t = useTranslations("Soulmachine");

  // Check for hash or URL search params that indicate help is needed
  const checkNeedsHelp = () => {
    if (typeof window === "undefined") return false;
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);

    return (
      hash === "#talk-with-ai-agent" ||
      hash === "#talk-with-emma" ||
      searchParams.get("help") === "true" ||
      searchParams.get("chat") === "true" ||
      searchParams.get("emma") === "true"
    );
  };

  const [shouldLoad, setShouldLoad] = useState(checkNeedsHelp());

  // Delay loading the widget to improve initial page load
  // But load immediately if hash or URL params indicate help is needed
  useEffect(() => {
    if (shouldLoad) {
      // Already set to load immediately, no need for delay
      return;
    }

    // Otherwise, delay loading
    const timer = setTimeout(() => {
      setShouldLoad(true);
    }, 30000); // Load after 30 seconds

    return () => clearTimeout(timer);
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;

    const createWidget = () => {
      const o = {
        smApiKey: process.env.NEXT_PUBLIC_SOULMACHINES_API_KEY,
        smGreeting: t("welcome"),
        smProfilePicture:
          "https://storage.googleapis.com/faxify-files/emma-faxify-ai-bot2.png",
      };

      // Remove existing widget if it exists
      const existingWidget = document.querySelector("sm-widget");

      if (existingWidget) {
        existingWidget.remove();
      }

      const widgetScript = document.createElement("sm-widget");

      if (o.smApiKey) widgetScript.setAttribute("api-key", o.smApiKey);
      if (o.smProfilePicture)
        widgetScript.setAttribute("profile-picture", o.smProfilePicture);
      if (o.smGreeting) widgetScript.setAttribute("greeting", o.smGreeting);

      // Add privacy-friendly attributes
      widgetScript.setAttribute("data-privacy-mode", "true");
      widgetScript.setAttribute("data-cookie-consent", "required");

      document.body.appendChild(widgetScript);

      return widgetScript;
    };

    const loadScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Check if script is already loaded
        const existingScript = document.querySelector(
          "script[src='https://static.soulmachines.com/web-components.1.12.0.js']"
        );

        if (existingScript) {
          // Script already loaded, resolve immediately
          resolve();

          return;
        }

        const script = document.createElement("script");

        script.src = "https://static.soulmachines.com/web-components.1.12.0.js";
        script.async = true;
        script.defer = true;

        script.onload = () => {
          resolve();
        };

        script.onerror = () => {
          reject(new Error("Failed to load Soulmachines script"));
        };

        const link = document.createElement("link");

        link.href = "https://static.soulmachines.com/web-components.1.12.0.css";
        link.rel = "stylesheet";
        link.type = "text/css";

        document.head.appendChild(script);
        document.head.appendChild(link);
      });
    };

    const ensureWidgetExists = () => {
      const widget = document.querySelector("sm-widget");

      if (!widget) {
        // Widget doesn't exist, try to create it
        try {
          createWidget();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to create widget:", error);
        }
      }
    };

    // Load script and create widget
    loadScript()
      .then(() => {
        // Script loaded successfully, create widget
        createWidget();

        // Verify widget exists after a short delay, retry if needed
        setTimeout(() => {
          ensureWidgetExists();
        }, 1000);

        // Additional check after longer delay to ensure widget persists
        setTimeout(() => {
          ensureWidgetExists();
        }, 5000);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error("Error loading Soulmachines script:", error);
        // Retry after delay
        setTimeout(() => {
          loadScript()
            .then(() => {
              createWidget();
            })
            .catch(retryError => {
              // eslint-disable-next-line no-console
              console.error("Retry failed:", retryError);
            });
        }, 5000);
      });

    function clickElementByAriaLabel(ariaLabel: any, delay = 500) {
      return new Promise((resolve, reject) => {
        const element = document.querySelector(`[aria-label="${ariaLabel}"]`);

        if (element) {
          // eslint-disable-next-line no-console
          console.log(`Clicking [aria-label="${ariaLabel}"]...`);
          (element as HTMLElement).click();

          // Simulate wait for click handler to complete with a timeout
          // eslint-disable-next-line no-console
          console.log(
            `Waiting for the delay=${delay} after clicking [aria-label="${ariaLabel}"]...`
          );
          setTimeout(resolve, delay);
        } else {
          reject(new Error(`Element with aria-label "${ariaLabel}" not found`));
        }
      });
    }

    const openTalk2AIAgent = (timeout = 5000) => {
      // Check for hash or URL search params
      const hash = window.location.hash;
      const searchParams = new URLSearchParams(window.location.search);
      const shouldOpen =
        hash === "#talk-with-ai-agent" ||
        hash === "#talk-with-emma" ||
        searchParams.get("help") === "true" ||
        searchParams.get("chat") === "true" ||
        searchParams.get("emma") === "true";

      if (shouldOpen) {
        // Wait for widget to be ready, then expand it
        const tryOpen = (attempts = 0) => {
          const widget = document.querySelector("sm-widget");
          const hideCardButton = document.querySelector(
            '[aria-label="Hide card"]'
          );
          const talkButton = document.querySelector(
            '[aria-label="Talk to a Digital Person"]'
          );

          if (widget && (hideCardButton || talkButton)) {
            // Widget is ready, expand it
            setTimeout(() => {
              if (hideCardButton) {
                clickElementByAriaLabel("Hide card", 1000)
                  .then(() =>
                    clickElementByAriaLabel("Talk to a Digital Person", 10000)
                  )
                  .then(() =>
                    clickElementByAriaLabel("Switch to fullframe layout", 2000)
                  )
                  .catch(error => {
                    // eslint-disable-next-line no-console
                    console.error("Error opening AI agent:", error);
                  });
              } else if (talkButton) {
                clickElementByAriaLabel("Talk to a Digital Person", 10000)
                  .then(() =>
                    clickElementByAriaLabel("Switch to fullframe layout", 2000)
                  )
                  .catch(error => {
                    // eslint-disable-next-line no-console
                    console.error("Error opening AI agent:", error);
                  });
              }
            }, timeout);
          } else if (attempts < 20) {
            // Widget not ready yet, retry after delay
            setTimeout(() => tryOpen(attempts + 1), 500);
          }
        };

        tryOpen();
      }
    };

    // Check immediately if hash/params are present
    openTalk2AIAgent(2000);

    // Also listen for hash changes
    const handleHashChange = () => {
      openTalk2AIAgent(2000);
    };

    window.addEventListener("hashchange", handleHashChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [shouldLoad, t]);

  // This component doesn't render anything visible
  return null;
}
